import React, { ReactElement, useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { FormGroup, FormGroupProps, FormGroupText } from '../CiscoUi';
import { MultipleSelectListbox } from './components/MultipleSelectListbox';
import { MultipleSelectTrigger } from './components/MultipleSelectTrigger';
import { useHandleClickOutside } from './hooks/use-handle-click-outside';
import styles from './index.module.css';
import { getTriggerLabels } from './utils/utils';

export type MultipleSelectOption = {
  name: string;
  value: string;
  divider?: boolean;
  selectAll?: boolean;
};

export type MultipleSelectProps = {
  id: string;
  label: string;
  labelClass?: string;
  options: MultipleSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: VoidFunction;
  errors?: FormGroupProps['errors'];
  required?: boolean;
  disabled?: boolean;
  horizontal?: boolean;
  className?: string;
  formGroupTextClass?: string;
  placeholder: string;
  testId?: string;
  onOpenChange?: (isOpen: boolean) => void;
};

export function MultipleSelect({
  className,
  disabled = false,
  errors = [],
  formGroupTextClass,
  horizontal = false,
  id,
  label,
  labelClass,
  onBlur,
  onChange,
  options,
  placeholder,
  required = false,
  testId,
  value,
  onOpenChange,
}: MultipleSelectProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = `${id}-listbox`;
  const labelId = `${id}-label`;

  const activeSelectAllOption = options.find(
    (option) => option.selectAll && value.includes(option.value),
  );
  const isSelectAllActive = Boolean(activeSelectAllOption);
  const { ariaLabel, displayText } = getTriggerLabels(
    value,
    options,
    placeholder,
    activeSelectAllOption,
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleToggle = useCallback(() => {
    setIsOpen((open) => {
      const nextOpen = !open;
      onOpenChange?.(nextOpen);

      return nextOpen;
    });
  }, [onOpenChange]);

  useHandleClickOutside({
    containerRef,
    isOpen,
    onClose: handleClose,
  });

  return (
    <FormGroup
      horizontal={horizontal}
      errors={errors}
      className={classnames(className, { active: isOpen })}
    >
      <div
        ref={containerRef}
        className={classnames('dropdown', styles.root, {
          active: isOpen,
          [styles.disabled]: disabled,
        })}
      >
        <FormGroupText className={classnames(formGroupTextClass, 'select')}>
          <label className={labelClass} htmlFor={id} id={labelId}>
            {label} {required && '*'}
          </label>
          <MultipleSelectTrigger
            id={id}
            listboxId={listboxId}
            ariaLabel={ariaLabel}
            displayText={displayText}
            isOpen={isOpen}
            disabled={disabled}
            testId={testId}
            onToggle={handleToggle}
            onBlur={onBlur}
          />
        </FormGroupText>
        <MultipleSelectListbox
          id={listboxId}
          labelId={labelId}
          fieldId={id}
          options={options}
          value={value}
          isSelectAllActive={isSelectAllActive}
          isOpen={isOpen}
          disabled={disabled}
          onChange={onChange}
        />
      </div>
    </FormGroup>
  );
}
